import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { User, UserRole } from '../types';
import { Plus, Search, Edit, Trash2, Shield, X, Save, Key, RefreshCw, Users as UsersIcon, Lock, Unlock, RotateCcw, Copy, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import './Users.css';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [mfaManagementUser, setMfaManagementUser] = useState<User | null>(null);
  const [mfaStatus, setMfaStatus] = useState<any>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['users', search],
    async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    }
  );

  const toggleActiveMutation = useMutation(
    async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.put(`/users/${id}`, { isActive });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('User status updated successfully');
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update user status');
      },
    }
  );

  const toggleMFAMutation = useMutation(
    async ({ id, enabled }: { id: string; enabled: boolean }) => {
      if (enabled) {
        // Enable MFA: use enable endpoint (will configure if needed or use existing)
        const response = await api.post(`/users/${id}/mfa/enable`, { reason: 'Admin enabled MFA via toggle' });
        return response.data;
      } else {
        // Disable MFA: use disable endpoint
        const response = await api.post(`/users/${id}/mfa/disable`, { reason: 'Admin disabled MFA via toggle' });
        return response.data;
      }
    },
    {
      onSuccess: (data, variables) => {
        if (variables.enabled) {
          toast.success('MFA enabled. User will need to set up MFA on next login.');
        } else {
          toast.success('MFA disabled successfully');
        }
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to toggle MFA');
      },
    }
  );

  const deleteMutation = useMutation(
    async (id: string) => {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data?.message || 'User deleted successfully');
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        console.error('Delete user error:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Failed to delete user';
        toast.error(errorMessage);
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('User updated successfully');
        queryClient.invalidateQueries('users');
        setEditingUser(null);
        setShowPasswordChange(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update user');
      },
    }
  );

  const passwordMutation = useMutation(
    async ({ id, password }: { id: string; password: string }) => {
      const response = await api.put(`/users/${id}/password`, { password });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Password updated successfully');
        setShowPasswordChange(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update password');
      },
    }
  );

  const createMutation = useMutation(
    async (data: any) => {
      const response = await api.post('/users', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.emailSent) {
          toast.success('User created successfully! Credentials have been sent via email.');
        } else {
          toast.success('User created successfully, but email sending failed. Please send credentials manually.', { duration: 5000 });
        }
        queryClient.invalidateQueries('users');
        setCreatingUser(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to create user');
      },
    }
  );

  const users: User[] = data?.users || [];
  
  // Calculate user statistics
  const userStats = {
    total: users.length,
    active: users.filter(u => u.isActive).length,
    inactive: users.filter(u => !u.isActive).length,
  };

  const handleToggleActive = (user: User) => {
    if (user.role === 'SUPER_ADMIN' || user.role === 'IT_ADMIN') {
      toast.error('Cannot change status of admin users. Use delete to deactivate.');
      return;
    }
    toggleActiveMutation.mutate({ id: user.id, isActive: !user.isActive });
  };

  const handleDelete = (user: User) => {
    const confirmMessage = user.role === 'SUPER_ADMIN' || user.role === 'IT_ADMIN'
      ? 'Admin users cannot be deleted. This will deactivate the user and preserve their shared resources. Continue?'
      : 'This will permanently delete the user and remove all their shared resources. This action cannot be undone. Continue?';
    
    if (window.confirm(confirmMessage)) {
      console.log('Deleting user:', user.id, user.email);
      deleteMutation.mutate(user.id, {
        onError: (error) => {
          console.error('Delete mutation error:', error);
        },
      });
    }
  };

  const handleManageMFA = async (user: User) => {
    setMfaManagementUser(user);
    try {
      const response = await api.get(`/users/${user.id}/mfa`);
      setMfaStatus(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load MFA status');
    }
  };

  const disableMFAMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/users/${id}/mfa/disable`, { reason });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('MFA disabled successfully');
        queryClient.invalidateQueries('users');
        if (mfaManagementUser) {
          handleManageMFA(mfaManagementUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to disable MFA');
      },
    }
  );

  const resetMFAMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/users/${id}/mfa/reset`, { reason });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('MFA reset successfully. New setup required on next login.');
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        queryClient.invalidateQueries('users');
        if (mfaManagementUser) {
          handleManageMFA(mfaManagementUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to reset MFA');
      },
    }
  );

  const regenerateBackupCodesMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/users/${id}/mfa/regenerate-backup-codes`, { reason });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Backup codes regenerated successfully');
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        if (mfaManagementUser) {
          handleManageMFA(mfaManagementUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to regenerate backup codes');
      },
    }
  );

  const revokeSessionMutation = useMutation(
    async ({ userId, sessionId }: { userId: string; sessionId: string }) => {
      const response = await api.delete(`/users/${userId}/sessions/${sessionId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Session revoked successfully');
        if (mfaManagementUser) {
          handleManageMFA(mfaManagementUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to revoke session');
      },
    }
  );

  const handleRevokeSession = (sessionId: string) => {
    if (mfaManagementUser && window.confirm('Are you sure you want to revoke this session? The user will be logged out from this device.')) {
      revokeSessionMutation.mutate({ userId: mfaManagementUser.id, sessionId });
    }
  };

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingUser) return;

    const formData = new FormData(e.currentTarget);
    const data: any = {};

    if (formData.get('email')) data.email = formData.get('email');
    if (formData.get('username')) data.username = formData.get('username');
    if (formData.get('firstName')) data.firstName = formData.get('firstName');
    if (formData.get('lastName')) data.lastName = formData.get('lastName');
    if (formData.get('role')) data.role = formData.get('role');
    if (formData.get('isActive') !== null) {
      data.isActive = formData.get('isActive') === 'on';
    }

    updateMutation.mutate({ id: editingUser.id, data });
  };

  const handlePasswordSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingUser) return;

    const formData = new FormData(e.currentTarget);
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    passwordMutation.mutate({ id: editingUser.id, password });
  };

  const handleGeneratePassword = async () => {
    setGeneratingPassword(true);
    try {
      const response = await api.get('/utils/generate-password?length=16');
      const generatedPassword = response.data.password;
      
      // Set password in form fields
      const passwordInput = document.querySelector('input[name="password"]') as HTMLInputElement;
      const confirmPasswordInput = document.querySelector('input[name="confirmPassword"]') as HTMLInputElement;
      
      if (passwordInput) passwordInput.value = generatedPassword;
      if (confirmPasswordInput) confirmPasswordInput.value = generatedPassword;
      
      toast.success('Password generated successfully!');
    } catch (error: any) {
      toast.error('Failed to generate password');
    } finally {
      setGeneratingPassword(false);
    }
  };

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      email: formData.get('email') as string,
      username: formData.get('username') as string,
      password: formData.get('password') as string,
      firstName: formData.get('firstName') as string || undefined,
      lastName: formData.get('lastName') as string || undefined,
      role: (formData.get('role') as UserRole) || 'STANDARD_USER',
    };

    const confirmPassword = formData.get('confirmPassword') as string;
    if (data.password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    createMutation.mutate(data);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return '#dc2626';
      case 'IT_ADMIN':
        return '#f59e0b';
      case 'STANDARD_USER':
        return '#3b82f6';
      case 'AUDITOR':
        return '#10b981';
      default:
        return '#64748b';
    }
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p>Manage system users and permissions</p>
        </div>
        <button className="btn-primary" onClick={() => setCreatingUser(true)}>
          <Plus size={20} />
          <span>Create User</span>
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#3b82f6' }}>
            <UsersIcon size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{userStats.total}</div>
            <div className="stat-label">Total Users</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#10b981' }}>
            <Shield size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{userStats.active}</div>
            <div className="stat-label">Active Users</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#ef4444' }}>
            <X size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{userStats.inactive}</div>
            <div className="stat-label">Inactive Users</div>
          </div>
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="empty-state">No users found</div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>MFA</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">
                        <Shield size={20} />
                      </div>
                      <div>
                        <div className="user-name">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.username}
                        </div>
                        <div className="user-username">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span
                      className="role-badge"
                      style={{ backgroundColor: getRoleBadgeColor(user.role) }}
                    >
                      {user.role.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>
                    <div className="status-cell">
                      <button
                        className={`toggle-switch ${user.isActive ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleActive(user)}
                        disabled={toggleActiveMutation.isLoading || (user.role === 'SUPER_ADMIN' || user.role === 'IT_ADMIN')}
                        title={user.role === 'SUPER_ADMIN' || user.role === 'IT_ADMIN' ? 'Admin users cannot be toggled. Use delete to deactivate.' : user.isActive ? 'Click to deactivate' : 'Click to activate'}
                      >
                        <span className="toggle-slider"></span>
                      </button>
                    <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                    </div>
                  </td>
                  <td>
                    <div className="mfa-status-cell">
                      <button
                        className={`toggle-switch ${user.mfaEnabled ? 'active' : 'inactive'}`}
                        onClick={() => {
                          // Prevent disabling own MFA
                          if (user.mfaEnabled && currentUser && user.id === currentUser.id) {
                            toast.error('Cannot disable your own MFA. Please use your profile settings.');
                            return;
                          }
                          const confirmMessage = user.mfaEnabled
                            ? 'This will disable MFA for this user and revoke all sessions. Continue?'
                            : 'This will enable MFA for this user. They will need to set up MFA on next login. Continue?';
                          if (window.confirm(confirmMessage)) {
                            toggleMFAMutation.mutate({ id: user.id, enabled: !user.mfaEnabled });
                          }
                        }}
                        disabled={toggleMFAMutation.isLoading || !!(user.mfaEnabled && currentUser && user.id === currentUser.id)}
                        title={
                          user.mfaEnabled && currentUser && user.id === currentUser.id
                            ? 'Cannot disable your own MFA'
                            : user.mfaEnabled
                            ? 'Click to disable MFA'
                            : 'Click to enable MFA'
                        }
                      >
                        <span className="toggle-slider"></span>
                      </button>
                      <span className={`mfa-badge ${user.mfaEnabled ? 'enabled' : 'disabled'}`}>
                        {user.mfaEnabled ? (
                          <>
                            <Lock size={14} />
                            <span>Enabled</span>
                          </>
                        ) : (
                          <>
                            <Unlock size={14} />
                            <span>Disabled</span>
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                  <td>
                    {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleString()
                      : 'Never'}
                  </td>
                  <td>
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="icon-btn"
                        onClick={() => setEditingUser(user)}
                        title="Edit user"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="icon-btn"
                        onClick={() => handleManageMFA(user)}
                        title="Manage MFA"
                      >
                        <Lock size={16} />
                      </button>
                      <button
                        className="icon-btn danger"
                        onClick={() => handleDelete(user)}
                        title={user.role === 'SUPER_ADMIN' || user.role === 'IT_ADMIN' ? 'Deactivate user and remove shared resources' : 'Delete user and remove shared resources'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal */}
      {creatingUser && (
        <div className="modal-overlay" onClick={() => setCreatingUser(false)}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button className="modal-close" onClick={() => setCreatingUser(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit} className="modal-form">
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" required placeholder="user@example.com" />
              </div>
              <div className="form-group">
                <label>Username *</label>
                <input type="text" name="username" required minLength={3} maxLength={50} placeholder="username" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input type="text" name="firstName" placeholder="John" />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input type="text" name="lastName" placeholder="Doe" />
                </div>
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select name="role" required defaultValue="STANDARD_USER">
                  <option value="STANDARD_USER">Standard User</option>
                  <option value="IT_ADMIN">IT Admin</option>
                  <option value="AUDITOR">Auditor</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Password *</label>
                <div className="password-input-group">
                  <input type="password" name="password" required minLength={8} placeholder="Minimum 8 characters" />
                  <button
                    type="button"
                    className="generate-password-btn"
                    onClick={handleGeneratePassword}
                    disabled={generatingPassword}
                    title="Generate secure password"
                  >
                    <RefreshCw size={16} className={generatingPassword ? 'spinning' : ''} />
                    <span>Generate</span>
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <input type="password" name="confirmPassword" required minLength={8} placeholder="Confirm password" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setCreatingUser(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={createMutation.isLoading}>
                  {createMutation.isLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={() => setEditingUser(null)}>
                <X size={24} />
              </button>
            </div>

            {!showPasswordChange ? (
              <>
                <form onSubmit={handleEditSubmit} className="modal-form">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      defaultValue={editingUser.email}
                    />
                  </div>
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      name="username"
                      required
                      minLength={3}
                      maxLength={50}
                      defaultValue={editingUser.username}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        defaultValue={editingUser.firstName || ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        defaultValue={editingUser.lastName || ''}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <select name="role" required defaultValue={editingUser.role}>
                      <option value="STANDARD_USER">Standard User</option>
                      <option value="IT_ADMIN">IT Admin</option>
                      <option value="AUDITOR">Auditor</option>
                      <option value="SUPER_ADMIN">Super Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        defaultChecked={editingUser.isActive}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowPasswordChange(true)}
                    >
                      <Key size={16} />
                      <span>Change Password</span>
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setEditingUser(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={updateMutation.isLoading}>
                      <Save size={16} />
                      <span>{updateMutation.isLoading ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <form onSubmit={handlePasswordSubmit} className="modal-form">
                  <div className="form-group">
                    <label>New Password *</label>
                    <div className="password-input-group">
                      <input
                        type="password"
                        name="password"
                        required
                        minLength={8}
                        placeholder="Minimum 8 characters"
                      />
                      <button
                        type="button"
                        className="generate-password-btn"
                        onClick={async () => {
                          setGeneratingPassword(true);
                          try {
                            const response = await api.get('/utils/generate-password?length=16');
                            const generatedPassword = response.data.password;
                            const passwordInput = document.querySelector('input[name="password"]') as HTMLInputElement;
                            const confirmPasswordInput = document.querySelector('input[name="confirmPassword"]') as HTMLInputElement;
                            if (passwordInput) passwordInput.value = generatedPassword;
                            if (confirmPasswordInput) confirmPasswordInput.value = generatedPassword;
                            toast.success('Password generated successfully!');
                          } catch (error: any) {
                            toast.error('Failed to generate password');
                          } finally {
                            setGeneratingPassword(false);
                          }
                        }}
                        disabled={generatingPassword}
                        title="Generate secure password"
                      >
                        <RefreshCw size={16} className={generatingPassword ? 'spinning' : ''} />
                        <span>Generate</span>
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Confirm Password *</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      required
                      minLength={8}
                      placeholder="Confirm password"
                    />
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowPasswordChange(false)}
                    >
                      Back
                    </button>
                    <button type="submit" className="btn-primary" disabled={passwordMutation.isLoading}>
                      {passwordMutation.isLoading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* MFA Management Modal */}
      {mfaManagementUser && mfaStatus && (
        <div className="modal-overlay" onClick={() => {
          setMfaManagementUser(null);
          setMfaStatus(null);
          setShowBackupCodes(false);
          setBackupCodes([]);
        }}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage MFA - {mfaStatus.name || mfaStatus.email}</h2>
              <button className="modal-close" onClick={() => {
                setMfaManagementUser(null);
                setMfaStatus(null);
                setShowBackupCodes(false);
                setBackupCodes([]);
              }}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-form">
              <div className="mfa-status-section">
                <h3>Current Status</h3>
                <div className="mfa-status-info">
                  <div className="info-row">
                    <span className="info-label">MFA Status:</span>
                    <span className={`mfa-badge ${mfaStatus.mfaEnabled ? 'enabled' : 'disabled'}`}>
                      {mfaStatus.mfaEnabled ? (
                        <>
                          <CheckCircle2 size={16} />
                          <span>Enabled</span>
                        </>
                      ) : (
                        <>
                          <X size={16} />
                          <span>Disabled</span>
                        </>
                      )}
                    </span>
                  </div>
                  {mfaStatus.mfaEnabled && (
                    <div className="info-row">
                      <span className="info-label">Backup Codes Available:</span>
                      <span>{mfaStatus.backupCodesCount} codes</span>
                    </div>
                  )}
                  {mfaStatus.activeSessions && mfaStatus.activeSessions.length > 0 && (
                    <div className="info-row">
                      <span className="info-label">Active Devices:</span>
                      <span>{mfaStatus.activeSessions.length} device(s)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Active Sessions/Devices Section */}
              {mfaStatus.activeSessions && mfaStatus.activeSessions.length > 0 && (
                <div className="sessions-section">
                  <h3>Active Devices & Sessions</h3>
                  <p className="sessions-info">
                    These are the devices currently logged in. You can revoke access to force logout from specific devices.
                  </p>
                  <div className="sessions-list">
                    {mfaStatus.activeSessions.map((session: any) => (
                      <div key={session.id} className="session-item">
                        <div className="session-info">
                          <div className="session-device-header">
                            <div className="session-device">
                              <Shield size={16} />
                              <div>
                                <div className="device-name">{session.deviceName || session.device || 'Unknown Device'}</div>
                                <div className="device-browser-os">
                                  {session.browser && <span className="device-browser">{session.browser}</span>}
                                  {session.os && <span className="device-os">{session.os}</span>}
                                  {session.deviceType && (
                                    <span className={`device-type ${session.deviceType.toLowerCase()}`}>
                                      {session.deviceType}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="session-ip-address">
                              {session.publicIP ? (
                                <>
                                  <div className="ip-address-row">
                                    <strong>Public IP:</strong> 
                                    <span className="ip-value public-ip">{session.publicIP}</span>
                                  </div>
                                  {session.localIP && (
                                    <div className="ip-address-row">
                                      <strong>Local IP:</strong> 
                                      <span className="ip-value local-ip">{session.localIP}</span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="ip-address-row">
                                  <strong>IP Address:</strong> 
                                  <span className="ip-value">{session.ipAddress || 'Unknown IP'}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="session-details">
                            <span className="session-time">
                              <strong>Connected:</strong> {new Date(session.createdAt).toLocaleString()}
                            </span>
                            {session.expiresAt && (
                              <span className="session-expires">
                                <strong>Expires:</strong> {new Date(session.expiresAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          className="btn-danger-small"
                          onClick={() => handleRevokeSession(session.id)}
                          title="Revoke this session"
                        >
                          <X size={14} />
                          <span>Revoke</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showBackupCodes && backupCodes.length > 0 && (
                <div className="backup-codes-section">
                  <h3>Backup Codes</h3>
                  <p className="backup-codes-warning">
                    ⚠️ Save these codes securely. They will not be shown again.
                  </p>
                  <div className="backup-codes-grid">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="backup-code-item">
                        <code>{code}</code>
                        <button
                          className="copy-code-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(code);
                            toast.success('Code copied to clipboard');
                          }}
                          title="Copy code"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      const allCodes = backupCodes.join('\n');
                      navigator.clipboard.writeText(allCodes);
                      toast.success('All codes copied to clipboard');
                    }}
                  >
                    <Copy size={16} />
                    <span>Copy All Codes</span>
                  </button>
                </div>
              )}

              <div className="mfa-actions-section">
                <h3>Actions</h3>
                <div className="mfa-actions-grid">
                  {mfaStatus.mfaEnabled ? (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          const reason = prompt('Reason for disabling MFA (optional):');
                          if (window.confirm('This will disable MFA for this user and revoke all sessions. Continue?')) {
                            disableMFAMutation.mutate({ id: mfaManagementUser.id, reason: reason || undefined });
                          }
                        }}
                        disabled={disableMFAMutation.isLoading}
                      >
                        <Unlock size={16} />
                        <span>{disableMFAMutation.isLoading ? 'Disabling...' : 'Disable MFA'}</span>
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          const reason = prompt('Reason for resetting MFA (optional):');
                          if (window.confirm('This will reset MFA and generate new secret and backup codes. User will need to set up MFA again on next login. Continue?')) {
                            resetMFAMutation.mutate({ id: mfaManagementUser.id, reason: reason || undefined });
                          }
                        }}
                        disabled={resetMFAMutation.isLoading}
                      >
                        <RotateCcw size={16} />
                        <span>{resetMFAMutation.isLoading ? 'Resetting...' : 'Reset MFA'}</span>
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          const reason = prompt('Reason for regenerating backup codes (optional):');
                          if (window.confirm('This will generate new backup codes. Old codes will no longer work. Continue?')) {
                            regenerateBackupCodesMutation.mutate({ id: mfaManagementUser.id, reason: reason || undefined });
                          }
                        }}
                        disabled={regenerateBackupCodesMutation.isLoading}
                      >
                        <RefreshCw size={16} />
                        <span>{regenerateBackupCodesMutation.isLoading ? 'Regenerating...' : 'Regenerate Backup Codes'}</span>
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        const reason = prompt('Reason for enabling MFA (optional):');
                        if (window.confirm('This will reset MFA and generate new secret and backup codes. User will need to set up MFA on next login. Continue?')) {
                          resetMFAMutation.mutate({ id: mfaManagementUser.id, reason: reason || undefined });
                        }
                      }}
                      disabled={resetMFAMutation.isLoading}
                    >
                      <Lock size={16} />
                      <span>{resetMFAMutation.isLoading ? 'Enabling...' : 'Enable MFA'}</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setMfaManagementUser(null);
                    setMfaStatus(null);
                    setShowBackupCodes(false);
                    setBackupCodes([]);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
